// Path: sec-parser/client/src/pages/ResultsPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Card, Button, Space, Select, Typography, Table,
  Row, Col, Tag, Statistic, Divider, message,
} from "antd";
import {
  DownloadOutlined, FileExcelOutlined,
  FileTextOutlined, CodeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined,
} from "@ant-design/icons";
import { getBenchmarkRun } from "../services/api";
import api from "../services/api";

const { Title, Text } = Typography;

const SECTIONS = [
  { key: "metadata",           label: "Company Overview" },
  { key: "incomeStatement",    label: "Income Statement" },
  { key: "balanceSheet",       label: "Balance Sheet" },
  { key: "cashFlowStatement",  label: "Cash Flow" },
  { key: "keyRatios",          label: "Key Ratios" },
  { key: "managementAnalysis", label: "MD&A Summary" },
  { key: "yoyComparison",      label: "YoY Changes" },
];

const FORMAT_BUTTONS = [
  { format: "xlsx", label: "Excel", icon: <FileExcelOutlined />, color: "#217346",
    mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ext: "xlsx" },
  { format: "csv",  label: "CSV",   icon: <FileTextOutlined />,  color: "#e67e22", mime: "text/csv", ext: "csv" },
  { format: "json", label: "JSON",  icon: <CodeOutlined />,      color: "#2980b9", mime: "application/json", ext: "json" },
];

// ── Authenticated download — avoids <a href> 401 → redirect-to-login bug ──
const downloadFile = async (resultId, format, ext, filename) => {
  try {
    const resp = await api.get(`/export/${resultId}/${format}`, { responseType: "blob" });
    const url  = URL.createObjectURL(new Blob([resp.data]));
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    message.error(`Download failed: ${err.response?.data?.error || err.message}`);
  }
};

export default function ResultsPage() {
  const { runId }    = useParams();
  const [params]     = useSearchParams();
  const defaultModel = params.get("model");

  const [runData, setRunData]             = useState(null);
  const [activeModel, setActiveModel]     = useState(defaultModel);
  const [activeSection, setActiveSection] = useState("metadata");
  const [downloading, setDownloading]     = useState(null);

  useEffect(() => {
    getBenchmarkRun(runId).then(({ data }) => {
      setRunData(data);
      if (!activeModel && data.results?.[0]) setActiveModel(data.results[0].modelId);
    });
  }, [runId]);

  const currentResult = runData?.results?.find((r) => r.modelId === activeModel);
  const sectionData   = currentResult?.data?.[activeSection] || {};
  const breakdown     = currentResult?.scoringBreakdown?.[activeSection];
  const company       = currentResult?.data?.metadata?.companyName || "Export";
  const period        = currentResult?.data?.metadata?.periodOfReport || "";
  const filenameBase  = `${company}_${period}_${activeModel || ""}`.replace(/[^a-zA-Z0-9_]/g, "_");

  const tableData = Object.entries(sectionData).map(([key, value]) => ({
    key,
    field:  key,
    value:  Array.isArray(value) ? value.join(" · ") : value,
    pts:    breakdown?.fields?.[key]?.points,
    status: breakdown?.fields?.[key]?.status || "⬜",
  }));

  const handleDownload = async (format, ext) => {
    if (!currentResult) return;
    setDownloading(format);
    await downloadFile(currentResult._id, format, ext, filenameBase);
    setDownloading(null);
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} wrap>
        <Col xs={24} md={12} style={{ marginBottom: 8 }}>
          <Title level={3} style={{ color: "#1A3C5E", margin: 0 }}>Parsed Results</Title>
          <Text type="secondary">
            {runData?.results?.length || 0} models · Run {runId?.slice(-6)}
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: "right" }}>
          <Select
            value={activeModel}
            onChange={setActiveModel}
            style={{ width: "100%", maxWidth: 280 }}
            options={(runData?.results || []).map((r) => ({
              value: r.modelId,
              label: `${r.modelId?.toUpperCase()} — ${r.accuracyScore ?? "?"}%`,
            }))}
          />
        </Col>
      </Row>

      {/* Score summary */}
      {currentResult && (
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          {[
            { title: "Score",    value: currentResult.accuracyScore, suffix: "%" },
            { title: "Filled",   value: currentResult.fieldsPopulated },
            { title: "Required", value: currentResult.requiredFieldsMet },
            { title: "Latency",  value: currentResult.latencyMs ? `${(currentResult.latencyMs/1000).toFixed(1)}s` : "—" },
            { title: "Cost",     value: currentResult.estimatedCostUsd ? `$${currentResult.estimatedCostUsd.toFixed(5)}` : "—" },
          ].map(({ title, value, suffix }) => (
            <Col key={title} xs={12} sm={8} md={5} flex="1">
              <Card size="small" style={{ textAlign: "center" }}>
                <Statistic title={title} value={value} suffix={suffix}
                  valueStyle={{ fontSize: 18, color: "#1A3C5E" }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Body: section nav + data table */}
      <Row gutter={[12, 12]}>
        {/* Section nav — horizontal scroll on mobile */}
        <Col xs={24} md={5}>
          <Card size="small" title="Sections" styles={{ body: { padding: "4px 0" } }}>
            {SECTIONS.map(({ key, label }) => {
              const pct = currentResult?.scoringBreakdown?.[key]?.completionPct;
              return (
                <div key={key} onClick={() => setActiveSection(key)} style={{
                  padding: "8px 12px", cursor: "pointer",
                  background: activeSection === key ? "#e6f0ff" : "transparent",
                  borderLeft: activeSection === key ? "3px solid #1A3C5E" : "3px solid transparent",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <Text style={{ fontSize: 12, color: activeSection === key ? "#1A3C5E" : "#444" }}>
                    {label}
                  </Text>
                  {pct !== undefined && (
                    <Tag color={pct >= 80 ? "green" : pct >= 50 ? "orange" : "red"}
                      style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>{pct}%</Tag>
                  )}
                </div>
              );
            })}
          </Card>
        </Col>

        {/* Data table */}
        <Col xs={24} md={19}>
          <Card
            title={SECTIONS.find((s) => s.key === activeSection)?.label}
            extra={breakdown && (
              <Tag color={breakdown.completionPct >= 80 ? "green" : "orange"}>
                {breakdown.earned?.toFixed(1)} / {breakdown.max} pts · {breakdown.completionPct}%
              </Tag>
            )}
          >
            <div style={{ overflowX: "auto" }}>
              <Table
                dataSource={tableData}
                pagination={false}
                size="small"
                scroll={{ x: 400 }}
                columns={[
                  {
                    title: "Field", dataIndex: "field", width: 200,
                    render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
                  },
                  {
                    title: "Value", dataIndex: "value",
                    render: (v) => {
                      if (v === null || v === undefined)
                        return <Text type="secondary">—</Text>;
                      if (typeof v === "number")
                        return <Text style={{ color: "#1A3C5E", fontWeight: 600 }}>{v.toLocaleString()}</Text>;
                      return <Text style={{ fontSize: 12 }}>{String(v).slice(0, 300)}</Text>;
                    },
                  },
                  {
                    title: "Status", dataIndex: "status", width: 70, align: "center",
                    render: (v, row) => {
                      if (v?.includes("✅")) return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
                      if (v?.includes("❌")) return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
                      return <Text type="secondary" style={{ fontSize: 10 }}>—</Text>;
                    },
                  },
                ]}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Export */}
      <Divider />
      <Card title={<><DownloadOutlined /> Export Report</>}>
        <Space wrap>
          {FORMAT_BUTTONS.map(({ format, label, icon, color, ext }) => (
            <Button
              key={format}
              size="large"
              icon={downloading === format ? <LoadingOutlined /> : icon}
              disabled={!currentResult || !!downloading}
              loading={downloading === format}
              onClick={() => handleDownload(format, ext)}
              style={{ borderColor: color, color: downloading ? undefined : color }}
            >
              {label}
            </Button>
          ))}
        </Space>
        <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
          Downloads the currently selected model's data. Export uses your login session — no redirect.
        </Text>
      </Card>
    </div>
  );
}